import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, FolderOpen, 
  LogOut, Menu, X, ChevronRight, Settings, Heart, Dna, AlertCircle, Folders, UserPlus, Quote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/access-requests', label: 'Access Requests', icon: UserPlus, showBadge: true },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/documents', label: 'Documents', icon: FileText },
  { path: '/admin/document-folders', label: 'Document Folders', icon: Folders },
  { path: '/admin/health-goals', label: 'Health Goals', icon: Heart },
  { path: '/admin/health-issues', label: 'Health Issues', icon: AlertCircle },
  { path: '/admin/genotypes', label: 'Genotypes', icon: Dna },
  { path: '/admin/testimonials', label: 'Testimonials', icon: Quote },
];

export function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);

  useEffect(() => {
    // Fetch pending access requests count
    const fetchPendingCount = async () => {
      try {
        const response = await adminAPI.getAccessRequests('pending');
        setPendingRequests(response.data.counts?.pending || 0);
      } catch (error) {
        console.error('Failed to fetch pending requests:', error);
      }
    };
    fetchPendingCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile Header */}
      <header className="lg:hidden bg-[#4A2C2A] text-white px-4 h-16 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-medium">Admin Panel</span>
        <div className="w-10" />
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 admin-sidebar">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <img src={LOGO_URL} alt="Rejuven" className="h-10 w-auto brightness-0 invert" />
                <button onClick={() => setSidebarOpen(false)} className="text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`admin-nav-item flex items-center gap-3 ${isActive ? 'active' : ''}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1">{item.label}</span>
                      {item.showBadge && pendingRequests > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-[#E8A095] text-white rounded-full">
                          {pendingRequests}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 admin-sidebar">
          <div className="flex flex-col flex-1 p-6">
            <Link to="/admin" className="mb-10">
              <img src={LOGO_URL} alt="Rejuven" className="h-12 w-auto brightness-0 invert" />
            </Link>
            
            <div className="mb-4">
              <span className="text-xs text-white/50 uppercase tracking-wider">Admin Panel</span>
            </div>
            
            <nav className="flex-1 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`admin-nav-item flex items-center gap-3 ${isActive ? 'active' : ''}`}
                    data-testid={`admin-nav-${item.path.split('/').pop() || 'dashboard'}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    {item.showBadge && pendingRequests > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-[#E8A095] text-white rounded-full">
                        {pendingRequests}
                      </span>
                    )}
                    {isActive && <ChevronRight className="w-4 h-4" />}
                  </Link>
                );
              })}
            </nav>

            <div className="pt-6 border-t border-white/10">
              <div className="flex items-center gap-3 px-4 py-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user?.name?.[0]?.toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{user?.name}</p>
                  <p className="text-sm text-white/50 truncate">Administrator</p>
                </div>
              </div>
              <Link to="/dashboard" className="admin-nav-item flex items-center gap-3 mb-2">
                <Settings className="w-5 h-5" />
                <span>User Dashboard</span>
              </Link>
              <button
                onClick={handleLogout}
                className="admin-nav-item flex items-center gap-3 w-full text-red-300 hover:text-red-200"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
